import { Language } from '../context/LanguageContext';
import { getLocalizedJewelleryCollectionItems, JewelleryCollectionItemView } from '../utils/jewelleryCollectionUtils';

export interface FluoringTranslations {
    mainTitle: string;
    mainSubtitle: string;
    fluorTitle: string;
    fluorSubtitle: string;
    fluorDescription: string;
    messageTitle: string;
    messageSubtitle: string;
    messageDescription: string;
    eternityTitle: string;
    eternitySubtitle: string;
    eternityDescription: string;
    collectionItems: Array<{
        id: string;
        name: string;
        description: string;
        image: string;
    }>;
}

type FluoringTemplateBase = Omit<FluoringTranslations, 'collectionItems'>;

const fluoringTemplateBaseTranslations: Partial<Record<Language, FluoringTemplateBase>> = {
    en: {
        mainTitle: "THE HIDDEN MESSAGES",
        mainSubtitle: "FROM INSIDE",
        fluorTitle: "FLUORESCENT",
        fluorSubtitle: "INITIALS",
        fluorDescription: "By placing natural fluorescent diamonds as script, you can embed a hidden message, like the names of two people in love, or a symbol. The message becomes visible only when the ring is exposed to UV light.",
        messageTitle: "THE MESSAGE",
        messageSubtitle: "FROM INSIDE",
        messageDescription: "The truth is hidden and is deep within. It shows its face only in specific circumstances. But in reality it is always there.",
        eternityTitle: "ETERNITY",
        eternitySubtitle: "RING",
        eternityDescription: "A ring always is a symbol of what you feel connected to. By giving it a specific direction, you can focus it and strengthen the connection."
    },
    de: {
        mainTitle: "DIE VERSTECKTEN BOTSCHAFTEN",
        mainSubtitle: "AUS DEM INNEREN",
        fluorTitle: "FLUORESZIERENDE",
        fluorSubtitle: "INITIALEN",
        fluorDescription: "Indem wir natürlich fluoreszierende Diamanten als Schriftzug einsetzen, können Sie eine versteckte Botschaft in die Ringe einbetten, z. B. die Namen zweier verliebter Menschen oder ein Symbol. Die Botschaft wird erst sichtbar, wenn der Ring mit UV-Licht bestrahlt wird.",
        messageTitle: "DIE NACHRICHT",
        messageSubtitle: "AUS DEM INNEREN",
        messageDescription: "Eine Botschaft die verborgen ist und sich nur unter bestimmten Umständen offenbart. Doch sowohl die Botschaft wie auch deren Bedeutung ist immer vorhanden.",
        eternityTitle: "ETERNITY",
        eternitySubtitle: "RING",
        eternityDescription: "Ein Ring ist immer ein Symbol für das, womit man sich verbunden fühlt. Indem man ihm eine bestimmte Bedeutung gibt, richtet man den Fokus auf deren Ursprung und stärkt die Verbindung."
    },
    fr: {
        mainTitle: "LES MESSAGES CACHÉS",
        mainSubtitle: "DE L'INTÉRIEUR",
        fluorTitle: "INITIALES",
        fluorSubtitle: "FLUORESCENTES",
        fluorDescription: "En plaçant des diamants naturellement fluorescents comme écriture, vous pouvez intégrer un message caché, comme les noms de deux personnes amoureuses, ou un symbole. Le message devient visible uniquement lorsque la bague est exposée à la lumière UV.",
        messageTitle: "LE MESSAGE",
        messageSubtitle: "DE L'INTÉRIEUR",
        messageDescription: "La vérité est cachée et se trouve au plus profond. Elle ne montre son visage que dans des circonstances spécifiques. Mais en réalité, elle est toujours là.",
        eternityTitle: "BAGUE",
        eternitySubtitle: "ÉTERNITÉ",
        eternityDescription: "Une bague est toujours un symbole de ce à quoi vous vous sentez connecté. En lui donnant une direction spécifique, vous pouvez concentrer et renforcer la connexion."
    },
    zh: {
        mainTitle: "內部隱藏的訊息",
        mainSubtitle: "",
        fluorTitle: "螢光字母",
        fluorSubtitle: "",
        fluorDescription: "按照一定的文字排列天然熒光鑽石，你可以嵌入隱藏訊息，例如兩位戀人的名字，或符號。這訊息只有在戒指暴露於紫外光下才會顯現。",
        messageTitle: "來自內部的信息",
        messageSubtitle: "",
        messageDescription: "真相隱藏在內心深處。只有在特定情況下它才會顯露出來。但實際上它一直在此。",
        eternityTitle: "永恆戒指",
        eternitySubtitle: "",
        eternityDescription: "戒指永遠是你與情感連結的象徵。透過為其給定具體方向，你可以集中情感並增強這種連結。"
    }
};

export function getFluoringTranslations(language: Language): FluoringTranslations {
    const base = fluoringTemplateBaseTranslations[language] || fluoringTemplateBaseTranslations.en!;
    const collectionItems = getLocalizedJewelleryCollectionItems('fluoring', language).map((item: JewelleryCollectionItemView) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        image: item.image
    }));

    return {
        ...base,
        collectionItems
    };
}
